import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Plane, Eye, EyeOff, ArrowLeft, Mail } from "lucide-react";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const getGoogleOAuthUrl = () => {
  const redirectUri = window.location.origin + '/auth/google';
  const state = crypto.randomUUID();
  sessionStorage.setItem('oauth_state', state);
  const params = new URLSearchParams({
    client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: 'openid email profile',
    state: state,
    prompt: 'select_account',
  });
  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
};

const LoginPage = () => {
  const [step, setStep] = useState(1); // 1 = choose method, 2 = email form, 3 = invite code
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, register, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim() || !password.trim()) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    if (!validateEmail(email)) {
      toast.error("Veuillez entrer une adresse courriel valide");
      return;
    }

    if (password.length < 4) {
      toast.error("Le mot de passe doit contenir au moins 4 caractères");
      return;
    }

    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
        toast.success("Bon retour!");
      } else {
        await register(email, password);
        toast.success("Compte créé avec succès!");
      }
      navigate("/");
    } catch (error) {
      const message = error.response?.data?.detail || "Une erreur est survenue";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = getGoogleOAuthUrl();
  };

  const handleEmailContinue = () => {
    setIsLogin(true);
    setStep(2);
  };

  const handleBack = () => {
    if (step === 3) {
      setStep(1);
      setInviteCode("");
    } else {
      setStep(1);
    }
    setIsLogin(true);
    setEmail("");
    setPassword("");
  };

  const handleInviteSubmit = () => {
    if (inviteCode.toLowerCase() === "friends") {
      setStep(2);
      setIsLogin(false);
    } else {
      toast.error("Code d'invitation invalide");
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1761254088615-3feefa51fe8c?crop=entropy&cs=srgb&fm=jpg&q=85')`,
        }}
      />
      
      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-black/70" />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#09090B] via-[#09090B]/60 to-transparent" />
      
      {/* Content */}
      <div className="relative z-10 w-full max-w-md">
        {/* Logo/Title */}
        <div className="text-center mb-8 animate-fadeIn">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/10 mb-4">
            <Plane className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-white tracking-tight">
            Forfaits Vacances Tracker
          </h1>
          <p className="text-white/60 mt-2 text-sm">
            Suivez les prix des forfaits vacances
          </p>
        </div>

        {/* Step 1: Choose Method */}
        {step === 1 && (
          <div 
            className="glass rounded-2xl p-8 animate-fadeIn"
            data-testid="auth-method-card"
          >
            <h2 className="font-heading text-xl font-semibold text-white mb-6 text-center">
              Connexion / Inscription
            </h2>

            <div className="space-y-4">
              {/* Google Login Button */}
              <Button
                type="button"
                onClick={handleGoogleLogin}
                className="w-full bg-white text-black hover:bg-white/90 font-semibold h-12 transition-transform active:scale-[0.98]"
                data-testid="google-login-button"
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continuer avec Google
              </Button>

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-transparent text-white/40">ou</span>
                </div>
              </div>

              {/* Email Login Button */}
              <Button
                type="button"
                onClick={handleEmailContinue}
                className="w-full bg-white/10 border border-white/20 text-white hover:bg-white/20 font-semibold h-12 transition-transform active:scale-[0.98]"
                data-testid="email-login-button"
              >
                <Mail className="w-5 h-5 mr-3" />
                Continuer avec courriel
              </Button>
            </div>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => setStep(3)}
                className="text-white/50 hover:text-white/70 text-sm transition-colors"
                data-testid="goto-register"
              >
                Pas encore de compte?{" "}
                <span className="text-white font-medium">S'inscrire</span>
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Invite Code */}
        {step === 3 && (
          <div 
            className="glass rounded-2xl p-8 animate-fadeIn"
            data-testid="invite-code-card"
          >
            <button
              type="button"
              onClick={handleBack}
              className="flex items-center text-white/50 hover:text-white/70 text-sm mb-4 transition-colors"
              data-testid="invite-back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </button>

            <h2 className="font-heading text-xl font-semibold text-white mb-2">
              Code d'invitation
            </h2>
            <p className="text-white/50 text-sm mb-6">
              Entrez votre code d'invitation pour créer un compte.
            </p>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="invite-code" className="text-white/80 text-sm">
                  Code
                </Label>
                <Input
                  id="invite-code"
                  data-testid="invite-code-input"
                  type="text"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleInviteSubmit()}
                  placeholder="Entrez le code"
                  className="bg-[#09090B] border-white/10 focus:border-white/30 text-white h-11 placeholder:text-white/20"
                  autoFocus
                />
              </div>

              <Button
                type="button"
                onClick={handleInviteSubmit}
                className="w-full bg-white text-black hover:bg-white/90 font-semibold h-11 transition-transform active:scale-[0.98]"
                data-testid="invite-submit-button"
              >
                Continuer
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Email Form */}
        {step === 2 && (
          <div 
            className="glass rounded-2xl p-8 animate-fadeIn"
            data-testid="auth-form-card"
          >
            {/* Back Button */}
            <button
              type="button"
              onClick={handleBack}
              className="flex items-center text-white/50 hover:text-white/70 text-sm mb-4 transition-colors"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </button>

            <h2 className="font-heading text-xl font-semibold text-white mb-6">
              {isLogin ? "Connexion" : "Créer un compte"}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white/80 text-sm">
                  Courriel
                </Label>
                <Input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre@courriel.com"
                  className="bg-[#09090B] border-white/10 focus:border-white/30 text-white h-11 placeholder:text-white/20"
                  autoComplete="email"
                  autoFocus
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-white/80 text-sm">
                  Mot de passe
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    data-testid="password-input"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Entrez votre mot de passe"
                    className="bg-[#09090B] border-white/10 focus:border-white/30 text-white h-11 pr-10 placeholder:text-white/20"
                    autoComplete={isLogin ? "current-password" : "new-password"}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60 transition-colors"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                data-testid="submit-button"
                disabled={loading}
                className="w-full bg-white text-black hover:bg-white/90 font-semibold h-11 transition-transform active:scale-[0.98]"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                ) : (
                  isLogin ? "Se connecter" : "Créer le compte"
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              {isLogin ? (
                <button
                  type="button"
                  onClick={() => { setStep(3); setEmail(""); setPassword(""); }}
                  className="text-white/50 hover:text-white/70 text-sm transition-colors"
                  data-testid="toggle-auth-mode"
                >
                  Pas encore de compte?{" "}
                  <span className="text-white font-medium">S'inscrire</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => { setIsLogin(true); setEmail(""); setPassword(""); }}
                  className="text-white/50 hover:text-white/70 text-sm transition-colors"
                  data-testid="toggle-auth-mode"
                >
                  Déjà un compte?{" "}
                  <span className="text-white font-medium">Se connecter</span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-white/30 text-xs mt-6 animate-fadeIn" style={{ animationDelay: '0.2s' }}>
          Suivez les changements de prix des forfaits tout inclus
        </p>
        <div className="flex justify-center gap-4 mt-3 animate-fadeIn" style={{ animationDelay: '0.3s' }}>
          <a href="/privacy" className="text-white/25 hover:text-white/50 text-xs transition-colors" data-testid="privacy-link">Confidentialite</a>
          <span className="text-white/15">|</span>
          <a href="/terms" className="text-white/25 hover:text-white/50 text-xs transition-colors" data-testid="terms-link">Conditions</a>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
